import { clamp } from "./audio-dsp.ts";
import { abortable, createDenoiser } from "./audio-denoise.ts";

export type AudioPreferences = { deviceId: string; denoise: boolean; noiseSuppression: boolean; echoCancellation: boolean; autoGainControl: boolean; gate: boolean; threshold: number; gain: number };
export type AudioFeedback = { db: number; peak: number; open: boolean; advanced: boolean; mode: "off" | "processed" | "native"; noiseSuppression?: boolean; echoCancellation?: boolean; warning: string };
export type VoicePipeline = { stream: MediaStream; close: () => void; update: (preferences: AudioPreferences) => Promise<void> };
export const defaultAudio: AudioPreferences = { deviceId: "", denoise: true, noiseSuppression: true, echoCancellation: true, autoGainControl: false, gate: true, threshold: -55, gain: 100 };
export const silentAudio: AudioFeedback = { db: -100, peak: 0, open: false, advanced: false, mode: "off", warning: "" };
export function normalizeAudio(value: Partial<AudioPreferences>): AudioPreferences {
  const result = { ...defaultAudio };
  for (const key of ["denoise", "noiseSuppression", "echoCancellation", "autoGainControl", "gate"] as const) if (typeof value[key] === "boolean") result[key] = value[key];
  if (typeof value.deviceId === "string" && value.deviceId.length <= 256) result.deviceId = value.deviceId;
  if (typeof value.threshold === "number" && Number.isFinite(value.threshold)) result.threshold = clamp(value.threshold, -65, -25);
  if (typeof value.gain === "number" && Number.isFinite(value.gain)) result.gain = clamp(value.gain, 0, 150);
  if (typeof value.denoise !== "boolean" && value.noiseSuppression === false) result.denoise = false;
  return result;
}
export function loadAudio(): AudioPreferences {
  try { return normalizeAudio(JSON.parse(localStorage.getItem("ecletica-audio") ?? "{}") as AudioPreferences); } catch { return { ...defaultAudio }; }
}
export function saveAudio(value: AudioPreferences) { try { localStorage.setItem("ecletica-audio", JSON.stringify(value)); } catch { /* Private browsing may disable storage. */ } }
function constraints(preferences: AudioPreferences, advanced = preferences.denoise): MediaTrackConstraints {
  return { noiseSuppression: advanced ? false : preferences.noiseSuppression, echoCancellation: preferences.echoCancellation, autoGainControl: preferences.autoGainControl, channelCount: { ideal: 1 } };
}

export async function createVoicePipeline(preferences: AudioPreferences, feedback: (state: AudioFeedback) => void, supplied?: MediaStream, signal?: AbortSignal): Promise<VoicePipeline> {
  const raw = supplied ?? await navigator.mediaDevices.getUserMedia({ audio: { ...constraints(preferences), ...(preferences.deviceId ? { deviceId: { exact: preferences.deviceId } } : {}) }, video: false });
  const track = raw.getAudioTracks()[0];
  if (!track) { raw.getTracks().forEach(track => track.stop()); throw new Error("O microfone não forneceu áudio."); }
  let context: AudioContext | undefined, source: MediaStreamAudioSourceNode | undefined, gate: AudioWorkletNode | undefined, filter: BiquadFilterNode | undefined, compressor: DynamicsCompressorNode | undefined, processed: MediaStream | undefined;
  let denoiser: Awaited<ReturnType<typeof createDenoiser>> | undefined;
  let current = preferences;
  const lifetime = new AbortController();
  let closed = false;
  let mode: AudioFeedback["mode"] = "native", warning = "";
  function close() { if (closed) return; closed = true; lifetime.abort(); signal?.removeEventListener("abort", close); track.removeEventListener?.("ended", ended); releaseGraph(); raw.getTracks().forEach(track => track.stop()); }
  function ended() { close(); feedback({ ...silentAudio, warning: "O microfone foi desconectado. Selecione um dispositivo e ligue novamente." }); }
  signal?.addEventListener("abort", close, { once: true });
  if (signal?.aborted) { close(); throw new DOMException("Captura cancelada", "AbortError"); }
  const report = (meter = { db: -100, peak: 0, open: false }) => {
    if (closed) return;
    const actual = track.getSettings();
    feedback({ ...meter, mode, warning, advanced: Boolean(denoiser), noiseSuppression: actual.noiseSuppression, echoCancellation: actual.echoCancellation });
  };
  function releaseGraph() {
    denoiser?.close(); denoiser = undefined;
    gate && (gate.port.onmessage = null);
    gate?.port.close(); source?.disconnect(); gate?.disconnect(); filter?.disconnect(); compressor?.disconnect();
    processed?.getTracks().forEach(track => track.stop());
    if (context) { void context.close().catch(() => {}); context = undefined; }
    gate = undefined; source = undefined; filter = undefined; compressor = undefined; processed = undefined;
  }
  async function restoreNative() {
    try { await abortable(track.applyConstraints(constraints(current, false)), lifetime.signal, 5000); }
    catch { warning += " O navegador não confirmou o ajuste de redução nativa."; }
  }
  function advancedFailed() {
    if (closed) return;
    denoiser?.close(); denoiser = undefined;
    filter?.disconnect();
    if (filter && (gate ?? compressor)) filter.connect((gate ?? compressor)!);
    warning = "A redução avançada foi interrompida. O microfone continua conectado com os filtros disponíveis do navegador.";
    report();
    void restoreNative().then(() => report());
  }
  track.addEventListener("ended", ended, { once: true });
  try {
    try { context = new AudioContext({ latencyHint: "interactive", sampleRate: 48000 }); }
    catch { context = new AudioContext({ latencyHint: "interactive" }); }
    await abortable(context.audioWorklet.addModule("/audio-gate.js"), lifetime.signal);
    if (closed) throw new DOMException("Captura cancelada", "AbortError");
    await abortable(context.resume(), lifetime.signal);
    if (closed) throw new DOMException("Captura cancelada", "AbortError");
    if (context.state !== "running") throw new Error("audio-suspended");
    source = context.createMediaStreamSource(raw);
    filter = context.createBiquadFilter(); filter.type = "highpass"; filter.frequency.value = 80;
    gate = new AudioWorkletNode(context, "ecletica-voice-gate", { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1], channelCount: 1, channelCountMode: "explicit" });
    compressor = context.createDynamicsCompressor(); compressor.threshold.value = -6; compressor.knee.value = 6; compressor.ratio.value = 12; compressor.attack.value = 0.003; compressor.release.value = 0.15;
    const destination = context.createMediaStreamDestination();
    processed = destination.stream;
    if (preferences.denoise) {
      try { denoiser = await createDenoiser(context, advancedFailed, lifetime.signal); }
      catch (error) {
        if (closed) throw error;
        warning = "Redução avançada indisponível. Usando os filtros do navegador e o corte entre falas.";
        await restoreNative();
      }
    }
    if (closed) throw new DOMException("Captura cancelada", "AbortError");
    source.connect(filter);
    if (denoiser) filter.connect(denoiser.node).connect(gate);
    else filter.connect(gate);
    gate.connect(compressor).connect(destination);
    mode = "processed";
    gate.port.postMessage({ enabled: preferences.gate, threshold: preferences.threshold, gain: preferences.gain });
    gate.port.onmessage = event => { const meter = event.data as { db: number; peak: number; open: boolean }; if (Number.isFinite(meter.db) && Number.isFinite(meter.peak) && typeof meter.open === "boolean") report(meter); };
    gate.onprocessorerror = () => {
      if (closed || !gate || !compressor || !filter) return;
      const upstream = denoiser?.node ?? filter;
      gate.port.onmessage = null; gate.port.close(); gate.disconnect(); gate = undefined;
      upstream.disconnect(); upstream.connect(compressor);
      warning = "O corte entre falas falhou e foi desativado para manter sua voz. O ganho adicional não está sendo aplicado.";
      report();
    };
  } catch (error) {
    if (closed) throw error;
    releaseGraph(); gate = undefined; processed = undefined;
    warning = "Filtro adicional indisponível. Usando somente os recursos de redução de ruído do navegador; ganho e corte de ruído não serão aplicados.";
    await restoreNative();
  }
  if (closed) throw new DOMException("Captura cancelada", "AbortError");
  const stream = processed ?? raw;
  report();
  return {
    stream, close,
    async update(next) {
      if (closed) return;
      if (next.denoise !== current.denoise || next.deviceId !== current.deviceId) throw new Error("Desligue o microfone antes de trocar o dispositivo ou a redução avançada.");
      const advanced = Boolean(denoiser);
      if ((["noiseSuppression", "echoCancellation", "autoGainControl"] as const).some(key => next[key] !== current[key])) await abortable(track.applyConstraints(constraints(next, advanced)), lifetime.signal, 5000);
      if (closed) return;
      current = next;
      if (advanced !== Boolean(denoiser)) await restoreNative();
      gate?.port.postMessage({ enabled: next.gate, threshold: next.threshold, gain: next.gain });
      report();
    },
  };
}
